import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Globe, MessageCircle, Phone, Mail, Lock, ExternalLink } from 'lucide-react';

const METHODS = [
  {
    key: 'website',
    icon: Globe,
    iconColor: 'text-white',
    title: 'Website',
    value: 'www.sansecurity.uk',
    description: 'Visit our official website to learn more about S.A.N Security Ltd and our services.',
    actionLabel: 'Open Website',
    href: 'https://www.sansecurity.uk',
    external: true,
  },
  {
    key: 'whatsapp',
    icon: MessageCircle,
    iconColor: 'text-green-400',
    title: 'WhatsApp',
    value: '07365 928174',
    description: 'Message us on WhatsApp — the fastest way to reach the team for shifts and quick questions.',
    actionLabel: 'Chat on WhatsApp',
    href: 'https://wa.me/447365928174',
    external: true,
  },
  {
    key: 'phone',
    icon: Phone,
    iconColor: 'text-white',
    title: 'Phone (Freephone)',
    value: '0800 707 4059',
    description: 'Call us free of charge. Available for urgent matters and general enquiries.',
    actionLabel: 'Call Now',
    href: 'tel:08007074059',
    external: false,
  },
  {
    key: 'email',
    icon: Mail,
    iconColor: 'text-white',
    title: 'Email',
    value: 'sansecuryty@gmail.com',
    description: 'Send us an email for applications, documents or anything that needs a written record.',
    actionLabel: 'Send Email',
    href: 'mailto:sansecuryty@gmail.com',
    external: false,
  },
];

export default function ContactPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#eef0f3] flex flex-col" data-testid="contact-page">
      {/* Dark brand header */}
      <header className="bg-[#14181f] text-white px-4 py-4 sm:py-6">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-3 sm:gap-4">
          <img
            src="/san-logo.jpg"
            alt="S.A.N Security"
            className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-sm shadow-[0_0_25px_rgba(250,204,21,0.3)]"
          />
          <div className="text-center sm:text-left">
            <h1 className="font-barlow tracking-widest text-xl sm:text-3xl leading-none">
              S.A.N <span className="text-white">SECURITY</span> <span className="text-primary">LTD</span>
            </h1>
            <p className="text-[10px] sm:text-sm tracking-[0.25em] text-primary uppercase mt-1 font-semibold">
              Your Safety. Our Priority.
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full max-w-3xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Back + title */}
        <button
          type="button"
          onClick={() => navigate('/login')}
          data-testid="contact-back-btn"
          className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>

        <div className="text-center mt-2 sm:mt-4">
          <h2 className="text-2xl sm:text-4xl font-bold text-zinc-900 tracking-tight">Contact Details</h2>
          <p className="text-sm sm:text-base text-zinc-500 mt-1">Get in touch with us — pick the way that suits you best</p>
        </div>

        {/* Method cards */}
        <div className="mt-4 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5">
          {METHODS.map((m) => (
            <div
              key={m.key}
              className="bg-white border border-zinc-200 rounded-lg shadow-sm p-4 sm:p-6 flex flex-col animate-fadeUp"
              data-testid={`contact-card-${m.key}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#14181f] flex items-center justify-center shrink-0">
                  <m.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${m.iconColor}`} strokeWidth={1.75} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm sm:text-base font-bold text-zinc-900">{m.title}</p>
                  <p className="text-sm sm:text-base text-primary font-semibold break-all">{m.value}</p>
                </div>
              </div>
              <p className="mt-2.5 text-xs sm:text-sm text-zinc-500 leading-relaxed flex-1">{m.description}</p>
              <Button
                asChild
                className="mt-3 w-full h-10 sm:h-11 bg-[#14181f] text-white hover:bg-[#232a35] rounded-md uppercase tracking-[0.15em] text-xs sm:text-sm font-semibold"
                data-testid={`contact-action-${m.key}`}
              >
                <a
                  href={m.href}
                  {...(m.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  {m.actionLabel}
                  {m.external && <ExternalLink className="w-3.5 h-3.5 ml-2" />}
                </a>
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#14181f] text-white px-4 py-3 sm:py-5 text-center">
        <p className="text-[10px] sm:text-xs text-white/50 flex items-center justify-center gap-1.5">
          <Lock className="w-3 h-3" /> S.A.N Security Ltd · Your Safety. Our Priority.
        </p>
      </footer>
    </div>
  );
}
